import { useEffect } from "react";

const SITE_NAME = "Afrodita Models";
const DEFAULT_TITLE = "Afrodita Models | Estudio Webcam en Armenia, Quindío — Afrodita";

/**
 * Origen canónico del sitio.
 *
 * El canónico se calculaba con `window.location.href`, así que en cualquier
 * despliegue que no fuera el dominio final (localhost, una preview, el mismo
 * sitio servido sin `www`) cada página se declaraba canónica de sí misma en el
 * host equivocado. Se fuerza el dominio público y se descarta la query, que
 * solo genera duplicados del mismo contenido.
 */
const SITE_URL = "https://www.afroditamodels.com";

const canonicalFor = (path) => `${SITE_URL}${path === "/" ? "/" : path.replace(/\/+$/, "")}`;

/**
 * Localiza (o crea) una etiqueta meta y devuelve una función para restaurarla.
 */
const setMeta = (selector, attrs) => {
    let element = document.head.querySelector(selector);
    let created = false;

    if (!element) {
        element = document.createElement(attrs.tag || 'meta');
        Object.entries(attrs.identity).forEach(([key, value]) => element.setAttribute(key, value));
        document.head.appendChild(element);
        created = true;
    }

    const previous = element.getAttribute(attrs.valueKey);
    element.setAttribute(attrs.valueKey, attrs.value);

    return () => {
        if (created) {
            element.remove();
        } else if (previous !== null) {
            element.setAttribute(attrs.valueKey, previous);
        }
    };
};

/**
 * Metadatos del documento por página.
 *
 * La SPA se renderiza en cliente, así que sin esto todas las rutas comparten el
 * título, la descripción y el canónico del index.html: los perfiles de las
 * modelas —el contenido con más potencial de tráfico orgánico— quedaban
 * indistinguibles entre sí para buscadores y para el previsualizador de enlaces
 * de redes sociales.
 *
 * Al desmontar se restaura el valor anterior para que la navegación entre
 * rutas no arrastre metadatos de la página previa.
 */
export const useDocumentMeta = ({
    title,
    description,
    canonical,
    image,
    type = 'website',
    keywords,
    noIndex = false,
    jsonLd = null,
} = {}) => {
    useEffect(() => {
        const cleanups = [];

        const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
        const previousTitle = document.title;
        document.title = fullTitle;
        cleanups.push(() => { document.title = previousTitle; });

        const url = canonical || canonicalFor(window.location.pathname);

        if (description) {
            cleanups.push(setMeta('meta[name="description"]', {
                identity: { name: 'description' }, valueKey: 'content', value: description,
            }));
            cleanups.push(setMeta('meta[property="og:description"]', {
                identity: { property: 'og:description' }, valueKey: 'content', value: description,
            }));
            cleanups.push(setMeta('meta[name="twitter:description"]', {
                identity: { name: 'twitter:description' }, valueKey: 'content', value: description,
            }));
        }

        cleanups.push(setMeta('link[rel="canonical"]', {
            tag: 'link', identity: { rel: 'canonical' }, valueKey: 'href', value: url,
        }));
        cleanups.push(setMeta('meta[property="og:url"]', {
            identity: { property: 'og:url' }, valueKey: 'content', value: url,
        }));
        cleanups.push(setMeta('meta[property="og:title"]', {
            identity: { property: 'og:title' }, valueKey: 'content', value: fullTitle,
        }));
        cleanups.push(setMeta('meta[name="twitter:title"]', {
            identity: { name: 'twitter:title' }, valueKey: 'content', value: fullTitle,
        }));
        cleanups.push(setMeta('meta[property="og:type"]', {
            identity: { property: 'og:type' }, valueKey: 'content', value: type,
        }));
        cleanups.push(setMeta('meta[property="og:site_name"]', {
            identity: { property: 'og:site_name' }, valueKey: 'content', value: SITE_NAME,
        }));
        cleanups.push(setMeta('meta[property="og:locale"]', {
            identity: { property: 'og:locale' }, valueKey: 'content', value: 'es_CO',
        }));

        // Las keywords del index.html describen la portada. Una ruta que aporta
        // las suyas (el perfil de una modelo, la vitrina) las sustituye en vez
        // de heredar las de otra página.
        if (keywords) {
            const value = Array.isArray(keywords) ? keywords.filter(Boolean).join(', ') : keywords;
            cleanups.push(setMeta('meta[name="keywords"]', {
                identity: { name: 'keywords' }, valueKey: 'content', value,
            }));
        }

        if (image) {
            cleanups.push(setMeta('meta[property="og:image"]', {
                identity: { property: 'og:image' }, valueKey: 'content', value: image,
            }));
            cleanups.push(setMeta('meta[name="twitter:image"]', {
                identity: { name: 'twitter:image' }, valueKey: 'content', value: image,
            }));
        }

        // Las páginas privadas o efímeras no deben indexarse.
        cleanups.push(setMeta('meta[name="robots"]', {
            identity: { name: 'robots' },
            valueKey: 'content',
            value: noIndex
                ? 'noindex, nofollow'
                : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        }));

        if (jsonLd) {
            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.dataset.dynamic = 'true';
            script.textContent = JSON.stringify(jsonLd);
            document.head.appendChild(script);
            cleanups.push(() => script.remove());
        }

        return () => cleanups.forEach((restore) => restore());
    }, [title, description, canonical, image, type, noIndex,
        Array.isArray(keywords) ? keywords.join(',') : keywords,
        JSON.stringify(jsonLd)]);
};
